interface DealPricingPlansProps {
  onSelectPlan: (plan: 'standard' | 'featured') => void;
  selectedPlan: 'standard' | 'featured' | null;
}

export function DealPricingPlans({ onSelectPlan, selectedPlan }: DealPricingPlansProps) {
  const plans = [
    {
      id: 'standard' as const,
      name: 'Standard Deal',
      price: '$25-50',
      period: 'per month',
      description: 'Perfect for regular promotions and seasonal offers',
      features: [
        'Listed on Deals page',
        'Unique coupon code',
        'Basic analytics dashboard',
        '30-day active listing',
        'Mobile-optimized display',
        'Social media sharing',
        'Email support'
      ],
      popular: false,
      color: 'emerald'
    },
    {
      id: 'featured' as const,
      name: 'Featured Deal',
      price: '$100-200',
      period: 'per month',
      description: 'Maximum visibility for your best offers',
      features: [
        'Everything in Standard',
        'Homepage featured spot',
        'Newsletter inclusion',
        '"Deal of the Week" badge',
        'Priority search placement',
        'Advanced analytics & insights',
        '60-day active listing',
        'Dedicated account manager',
        'A/B testing support'
      ],
      popular: true,
      color: 'teal'
    }
  ];

  const scrollToForm = (planId: 'standard' | 'featured') => {
    onSelectPlan(planId);
    setTimeout(() => {
      const formElement = document.getElementById('deal-submission-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Deal Promotion Plan
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Select the plan that best fits your marketing goals. All plans include redemption tracking and can be upgraded anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white rounded-2xl border-2 p-8 hover:shadow-2xl transition-all duration-300 ${
                plan.popular ? 'border-teal-500 shadow-xl' : 'border-gray-200'
              } ${selectedPlan === plan.id ? 'ring-4 ring-emerald-200' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <p className="text-gray-600 text-sm mb-6">{plan.description}</p>
                <div className="mb-2">
                  <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                </div>
                <p className="text-gray-500 text-sm">{plan.period}</p>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className={`ri-check-line text-${plan.color}-600 text-xl flex-shrink-0 mt-0.5`}></i>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => scrollToForm(plan.id)}
                className={`w-full py-4 rounded-xl font-bold transition-all duration-300 whitespace-nowrap ${
                  plan.popular
                    ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white hover:shadow-lg hover:scale-105'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                Get Started with {plan.name}
              </button>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-8 max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-percent-line text-2xl text-amber-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Commission-Based Option Available
              </h3>
              <p className="text-gray-700 mb-4">
                Prefer to pay only when customers redeem? Choose our commission model: <strong>10-15% per redemption</strong> with unique tracking codes. Perfect for high-value deals or testing new promotions.
              </p>
              <p className="text-sm text-gray-600">
                Contact us after submission to switch to commission-based pricing.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}